import type { ReactNode } from 'react';

import { ROLE_LABEL, type Role } from '@/constants/roles';
import { formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { InstituteHeadcount, InstituteSignedIn } from '@/types/institute';

export interface HeadcountStripProps {
  headcount: InstituteHeadcount;
  signedIn: InstituteSignedIn;
  /** Drawn at the right of the signed-in line — the card's "View more reports". */
  action?: ReactNode;
  className?: string;
}

/**
 * Admins, students, teachers and staff, with adoption underneath.
 *
 * Its own component so any screen that later needs these four numbers draws
 * them the way the customer card does. Staff carries its split by role on hover, and for
 * screen readers in the value itself, because "6 staff" invites "6 what?".
 *
 * The signed-in line counts people who have ever signed in, not sign-in events
 * — only the most recent login is recorded, so a count of logins does not
 * exist. Split by kind because a school where four hundred students have
 * signed in and no teachers has a problem a total hides.
 */
export function HeadcountStrip({ headcount, signedIn, action, className }: HeadcountStripProps) {
  const stats = [
    // Administrators and principals, as the per-customer report counts them —
    // "staff" is everyone else on the payroll who is not a teacher.
    { label: 'Admins', value: headcount.admins, detail: 'Administrators and principals' },
    { label: 'Students', value: headcount.students, detail: null },
    { label: 'Teachers', value: headcount.teachers, detail: null },
    { label: 'Staff', value: headcount.staff.total, detail: describeStaff(headcount.staff.byRole) },
  ];

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <dl className="grid grid-cols-4 divide-x divide-slate-200 dark:divide-slate-800 overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-800/40">
        {stats.map((stat) => (
          <div key={stat.label} className="px-1 py-2.5 text-center" title={stat.detail ?? undefined}>
            <dd
              className={cn(
                'text-[17px] font-black tabular-nums leading-none',
                stat.value === 0 ? 'text-slate-400 dark:text-slate-600' : 'text-slate-900 dark:text-white',
              )}
            >
              {formatNumber(stat.value)}
              {stat.detail && <span className="sr-only"> ({stat.detail})</span>}
            </dd>
            <dt className="mt-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{stat.label}</dt>
          </div>
        ))}
      </dl>

      <div className="flex items-center justify-between gap-2 text-xs">
        <span className="min-w-0 truncate text-slate-500 dark:text-slate-400 font-medium">
          Signed in:{' '}
          <span className="font-bold tabular-nums text-slate-800 dark:text-slate-200">{formatNumber(signedIn.students)}</span> students ·{' '}
          <span className="font-bold tabular-nums text-slate-800 dark:text-slate-200">{formatNumber(signedIn.teachers)}</span> teachers
        </span>
        {action}
      </div>
    </div>
  );
}

/**
 * "3 drivers, 1 accountant" — the staff split in words, largest group first.
 * Null when there is nobody to describe, so the cell gets no empty tooltip.
 */
function describeStaff(byRole: Partial<Record<Role, number>>): string | null {
  const parts = (Object.entries(byRole) as [Role, number][])
    .filter(([, count]) => count > 0)
    .sort(([, a], [, b]) => b - a)
    .map(([role, count]) => {
      const label = ROLE_LABEL[role].toLowerCase();
      return `${formatNumber(count)} ${count === 1 ? label : `${label}s`}`;
    });

  return parts.length > 0 ? parts.join(', ') : null;
}
